/**
 * SharedByMeList Component
 * Displays parlays that the current user has shared with others
 * Includes ability to unshare with individual recipients
 */

import LoadingSpinner from '@components/ui/LoadingSpinner';
import { type ShareByMe, useSharing } from '@hooks/useSharing';
import { formatTimeAgo } from '@parlay-picker/shared/formatting';
import { ChevronDown, ChevronUp, Eye, Send, User, X } from 'lucide-react';
import { type ReactElement, useEffect, useMemo, useState } from 'react';

// =============================================================================
// Helper Functions
// =============================================================================

function getResultBadgeStyle(result: string): string {
  switch (result) {
    case 'won':
      return 'bg-theme-success/20 text-theme-success border-theme-success/30';
    case 'lost':
      return 'bg-theme-danger/20 text-theme-danger border-theme-danger/30';
    case 'void':
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    case 'push':
      return 'bg-theme-surface-hover/20 text-theme-text-secondary border-gray-500/30';
    default:
      return 'bg-theme-warning/20 text-theme-warning border-theme-warning/30';
  }
}

// =============================================================================
// Sub-components
// =============================================================================

interface RecipientItemProps {
  recipient: ShareByMe['recipients'][0];
  parlayId: number;
  onUnshare: (parlayId: number, userId: string) => Promise<boolean>;
}

function RecipientItem({ recipient, parlayId, onUnshare }: RecipientItemProps): ReactElement {
  const [confirming, setConfirming] = useState(false);
  const [unsharing, setUnsharing] = useState(false);

  const handleUnshare = async () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }

    setUnsharing(true);
    await onUnshare(parlayId, recipient.recipientId);
    setUnsharing(false);
    setConfirming(false);
  };

  const handleCancel = () => {
    setConfirming(false);
  };

  return (
    <div className="flex items-center justify-between py-2 px-3 bg-theme-surface/50 rounded-lg">
      <div className="flex items-center gap-2">
        {/* Avatar placeholder */}
        <div className="w-7 h-7 rounded-full bg-theme-surface-hover flex items-center justify-center">
          <User className="w-3.5 h-3.5 text-theme-text-secondary" />
        </div>
        <div>
          <span className="text-sm text-gray-200">
            {recipient.recipientDisplayName || recipient.recipientUsername}
          </span>
          <div className="flex items-center gap-2 text-xs text-theme-text-tertiary">
            <span>{formatTimeAgo(recipient.sharedAt)}</span>
            {recipient.viewedAt && (
              <span className="flex items-center gap-1 text-theme-success/70">
                <Eye className="w-3 h-3" />
                Viewed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Unshare button */}
      {confirming ? (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleCancel}
            disabled={unsharing}
            className="px-2 py-1 text-xs text-theme-text-secondary hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUnshare}
            disabled={unsharing}
            className="px-2 py-1 text-xs bg-theme-danger/20 text-theme-danger hover:bg-theme-danger/30 rounded transition-colors disabled:opacity-50"
          >
            {unsharing ? 'Removing...' : 'Confirm'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleUnshare}
          className="p-1.5 text-theme-text-tertiary hover:text-theme-danger hover:bg-theme-danger/10 rounded transition-colors"
          title="Unshare"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

interface SharedParlayCardProps {
  share: ShareByMe;
  onUnshare: (parlayId: number, userId: string) => Promise<boolean>;
}

function SharedParlayCard({ share, onUnshare }: SharedParlayCardProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const viewedCount = share.recipients.filter((r) => r.viewedAt !== null).length;

  return (
    <div className="bg-theme-surface/50 rounded-lg border border-theme-border overflow-hidden">
      {/* Header - clickable to expand */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 text-left hover:bg-theme-surface/70 transition-colors"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-theme-primary" />
            <span className="text-sm font-medium text-gray-200">
              {share.parlayName || 'Untitled Parlay'}
            </span>
          </div>
          <span
            className={`text-xs px-2 py-0.5 rounded border font-medium ${getResultBadgeStyle(share.result)}`}
          >
            {share.result.toUpperCase()}
          </span>
        </div>

        {/* Legs preview */}
        <div className="space-y-1 mb-3">
          {share.legs.slice(0, 2).map((leg, i) => (
            <div key={i} className="text-sm text-theme-text-secondary flex items-center gap-2">
              <span className="text-theme-text-tertiary">•</span>
              <span>
                {leg.player} {leg.prop}
              </span>
            </div>
          ))}
          {share.legs.length > 2 && (
            <div className="text-xs text-theme-text-tertiary">
              +{share.legs.length - 2} more leg
              {share.legs.length - 2 > 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-theme-text-tertiary">
          <span>
            {share.legs.length} legs · {share.totalOdds}
          </span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {share.recipients.length} recipient
              {share.recipients.length !== 1 ? 's' : ''}
              {viewedCount > 0 && (
                <span className="text-theme-success/70">({viewedCount} viewed)</span>
              )}
            </span>
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-theme-text-secondary" />
            ) : (
              <ChevronDown className="w-4 h-4 text-theme-text-secondary" />
            )}
          </div>
        </div>
      </button>

      {/* Expanded recipients list */}
      {expanded && (
        <div className="border-t border-theme-border p-3 space-y-2">
          <div className="text-xs text-theme-text-secondary mb-2">Shared with:</div>
          {share.recipients.map((recipient) => (
            <RecipientItem
              key={recipient.recipientId}
              recipient={recipient}
              parlayId={share.parlayId}
              onUnshare={onUnshare}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function SharedByMeList(): ReactElement {
  const { sharedByMe, fetchSharedByMe, unshareParlay, sharedByMeLoading, sharedByMeError } =
    useSharing();

  useEffect(() => {
    fetchSharedByMe();
  }, [fetchSharedByMe]);

  // Sort by most recent share activity (memoized — must be before all early returns for rules-of-hooks)
  const { sortedShares, totalRecipients } = useMemo(() => {
    const sorted = [...sharedByMe].sort((a, b) => {
      const aLatest = Math.max(...a.recipients.map((r) => new Date(r.sharedAt).getTime()));
      const bLatest = Math.max(...b.recipients.map((r) => new Date(r.sharedAt).getTime()));
      return bLatest - aLatest;
    });
    const total = sharedByMe.reduce((sum, s) => sum + s.recipients.length, 0);
    return { sortedShares: sorted, totalRecipients: total };
  }, [sharedByMe]);

  if (sharedByMeLoading && sharedByMe.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (sharedByMeError) {
    return (
      <div className="bg-theme-danger/10 border border-theme-danger/30 rounded-lg p-4 text-theme-danger">
        Failed to load shared parlays: {sharedByMeError}
      </div>
    );
  }

  if (sharedByMe.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">📤</div>
        <h2 className="text-xl font-semibold text-theme-text-secondary mb-2">No Shared Parlays</h2>
        <p className="text-theme-text-secondary">
          When you share a parlay with someone, it will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-theme-text-secondary mb-2">
        {sharedByMe.length} parlay{sharedByMe.length !== 1 ? 's' : ''} shared with {totalRecipients}{' '}
        recipient{totalRecipients !== 1 ? 's' : ''}
      </div>

      {sortedShares.map((share) => (
        <SharedParlayCard key={share.parlayId} share={share} onUnshare={unshareParlay} />
      ))}
    </div>
  );
}
